import { useHistory } from 'react-router';
import { PATHS, BASE_PATHS, template } from '@belong/common';
import { LinkBase, Text, Button as ButtonUI } from '@belong/ui';
import {
  awaitingCosignersCopy,
  getInvitationSentToDoTitle,
  signedProgress,
} from 'action-items/invitation-sent.utils';
import { ANALYTICS_CLICK_ACTIONS } from 'analytics';
import cx from 'classnames';
import Button, { BUTTON_TYPES } from 'components/Button/Button';
import { GENERAL_ICONS } from 'components/GeneralIcon/GeneralIcon';
import IconButton, { ALIGN_TYPES } from 'components/IconButton/IconButton';
import { UserToDoTypes, StepUrlPaths } from 'models/enums';
import { findFirstOpenStep } from 'pages/AgreementFlow/utils';
import PropTypes from 'prop-types';
import { TO_DO_STRINGS_MAP } from 'strings/to-do.strings';
import { pluralizeWithoutValue } from 'utils/pluralize';

export default function ToDoComponents({
  type,
  metadata = {},
  properties,
  openPricingModal,
  todos,
  homes,
}) {
  const history = useHistory();

  const todoType = type;
  let linkPath = '';
  let label;
  let showIconButtonInRender = true;

  function handleW9ToDoClick() {
    history.push(PATHS.W9_FLOW.replace(':flowId', metadata.flowId));
  }

  switch (type) {
    case UserToDoTypes.SetupEarningsPaymentMethod:
      linkPath = PATHS.HOMEOWNER_ACCOUNT_EARNINGS;
      break;
    case 'HomeownerSetupFlowOpen': {
      const propertyWithoutSetupFlowCompleted = properties?.filter(
        (property) => {
          if (property.getUnitsWithAgreements().length) {
            return property.units.some((unit) => !unit.isSetupFlowCompleted);
          }

          return false;
        },
      );

      if (properties?.length > 1) {
        showIconButtonInRender = false;

        label = (
          <div className="flex w-full flex-col">
            {propertyWithoutSetupFlowCompleted.map((property, index) => {
              linkPath = `${BASE_PATHS.HOMEOWNER_SETUP_FLOW}/${property?.basicInfo.propertyId}`;

              return (
                <div
                  key={`set-up-link-${property?.basicInfo.propertyId}`}
                  className={cx('flex justify-between items-center', {
                    'mb-xs':
                      index + 1 !== propertyWithoutSetupFlowCompleted.length,
                  })}
                >
                  <LinkBase href={linkPath}>
                    <Text as="span" fontWeight="semibold">
                      SET UP{' '}
                    </Text>
                    <Text as="span">{property?.address.streetAddress}</Text>
                  </LinkBase>
                  <div className="flex">
                    <IconButton
                      align={ALIGN_TYPES.RIGHT}
                      to={linkPath}
                      icon={GENERAL_ICONS.FRONT_ARROW}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        );
      } else {
        linkPath = `${BASE_PATHS.HOMEOWNER_SETUP_FLOW}/${properties?.[0]?.basicInfo.propertyId}`;

        label = (
          <LinkBase href={linkPath}>
            <Text as="span" fontWeight="semibold">
              SET UP YOUR HOME
            </Text>
          </LinkBase>
        );
      }

      break;
    }
    case UserToDoTypes.AgreementFlowOpen: {
      const agreementFlowOpenTodos = todos.filter(
        (todo) => todo.type === UserToDoTypes.AgreementFlowOpen,
      );
      const { flowId, flowStepId } = metadata;

      const currentTodo = agreementFlowOpenTodos.find(
        (todo) => todo.metadata.flowStepId === flowStepId.toString(),
      );

      const nextStep = findFirstOpenStep(currentTodo?.steps);

      linkPath = nextStep
        ? `${BASE_PATHS.AGREEMENT_FLOW}/${StepUrlPaths[nextStep.stepName]}/${flowId}/${nextStep.uniqueId}`
        : `${BASE_PATHS.AGREEMENT_FLOW}/plans/${flowId}/${flowStepId}`;

      const [firstHomeInAgreement] = homes;

      const title = getInvitationSentToDoTitle({
        metadata,
        invitationTitle: TO_DO_STRINGS_MAP[todoType],
      });

      const subtitleLines = [
        signedProgress(metadata),
        awaitingCosignersCopy(metadata),
      ].filter(Boolean);

      const subtitle = subtitleLines.map((line) => (
        <Text
          key={line}
          as="span"
          variant="p1"
          className="block uppercase text-dark-gray"
        >
          {line}
        </Text>
      ));

      label =
        homes.length > 1 ? (
          <LinkBase href={linkPath}>
            <Text as="span" className="uppercase" fontWeight="semibold">
              {title}
            </Text>{' '}
            <Text as="span" className="uppercase" fontWeight="semibold">
              FOR {firstHomeInAgreement.address} + {homes.length - 1} other{' '}
              {pluralizeWithoutValue(homes.length - 1, 'home')}
            </Text>
            {subtitle}
          </LinkBase>
        ) : (
          <LinkBase href={linkPath}>
            <Text as="span" className="uppercase" fontWeight="semibold">
              {title}
            </Text>{' '}
            <Text as="span" className="uppercase" fontWeight="semibold">
              FOR {firstHomeInAgreement.address}
            </Text>
            {subtitle}
          </LinkBase>
        );

      break;
    }
    case UserToDoTypes.PendingLeaseSignature:
      linkPath = metadata.cta
        ? `/${metadata.cta}`
        : `${PATHS.LEASE_SIGNING_FLOW_V2}/GetStarted?useLatestStep=true`;
      break;
    case UserToDoTypes.InitialPriceReportReview: {
      const unitProperty = properties?.find((property) =>
        property.units.some(
          (unit) => unit?.basicInfo?.id.toString() === metadata?.unitId,
        ),
      );
      const currentUnit = unitProperty?.units?.find(
        (unit) => unit?.basicInfo?.id.toString() === metadata?.unitId,
      );

      if (currentUnit?.isInitialPriceRangePublished) {
        label = (
          <Button
            action={ANALYTICS_CLICK_ACTIONS.INITIAL_ESTIMATE_OPEN}
            onClick={() => openPricingModal(currentUnit, unitProperty?.address)}
            buttonType={BUTTON_TYPES.NOSTYLE}
          >
            <Text fontWeight="semibold">{`${TO_DO_STRINGS_MAP[todoType]} - ${
              unitProperty?.address?.streetAddress
            } - Unit ${currentUnit?.basicInfo?.unitNumber || '1'}`}</Text>
          </Button>
        );
      } else {
        return null;
      }

      break;
    }
    case UserToDoTypes.WealthAnalysisFlowOpen: {
      label = (
        <LinkBase
          href={`${PATHS.HOMEOWNER_ACCOUNT_WEALTH_ANALYSIS_FLOW}?flowId=${metadata.flowId}`}
          className="max-w-prose"
        >
          <Text as="span" fontWeight="semibold">
            {template(TO_DO_STRINGS_MAP[todoType], {
              address: metadata.homeAddress.address,
            })}
          </Text>
        </LinkBase>
      );
      break;
    }
    case 'W9FlowOpen':
      label = (
        <ButtonUI onClick={handleW9ToDoClick} variant="text">
          <Text className="text-left" fontWeight="semibold">
            {TO_DO_STRINGS_MAP[todoType]} FOR {metadata.legalEntityName}
          </Text>
        </ButtonUI>
      );

      break;
    case UserToDoTypes.SetupRentPaymentMethod:
    default:
      linkPath = PATHS.RESIDENTS_ACCOUNT_PAYMENTS;
      break;
  }

  if (!label) {
    label = (
      <LinkBase href={linkPath}>
        <Text fontWeight="semibold">{TO_DO_STRINGS_MAP[todoType]}</Text>
      </LinkBase>
    );
  }

  return (
    <div className="flex items-center justify-between">
      {label}
      {showIconButtonInRender && (
        <div className="flex">
          <IconButton
            align={ALIGN_TYPES.RIGHT}
            to={linkPath}
            icon={GENERAL_ICONS.FRONT_ARROW}
          />
        </div>
      )}
    </div>
  );
}

ToDoComponents.propTypes = {
  type: PropTypes.string.isRequired,
  metadata: PropTypes.object,
  properties: PropTypes.array,
  openPricingModal: PropTypes.func,
  todos: PropTypes.array.isRequired,
  homes: PropTypes.array,
};
